<template>
  <div class="landing-wrap">
    <!-- El shadow root cuelga de acá (ver shadowMount.js). Sin overflow:hidden
         en ningún ancestro: la cabecera y las franjas de producto usan
         position:sticky, y un ancestro que recorta las apaga. -->
    <div ref="host" class="landing-host"></div>
    <p v-if="error" class="landing-error">
      No pudimos cargar la portada.
      <a href="/utopie/">Vuelve a intentarlo</a>.
    </p>
  </div>
</template>

<script>
// La portada de la app: la propuesta plataforma_pie_propuesta_3.
//
// El marcado y la hoja los sirve Django (views.landing_secciones, que renderiza
// marketing/_landing.html con {% static %}) y se montan en un shadow root: la
// hoja trae `.btn`, `.hero`, `.top` o `.shell`, que chocan de frente con
// Bootstrap. Las animaciones son los guiones de la propuesta, corriendo contra
// ese shadow root (ver landingEngine.js).
//
// En esta ruta el layout esconde su hero y sus botones de producto, y para
// quien no tiene sesión también su cabecera: la portada trae la suya. Con
// sesión se queda la cabecera de la app y la de la portada se apaga
// (`.con-sesion`).
import informationService from "@/services/informationService";
import {
  medirClicDePortada,
  trackLeadDeLaPortada,
} from "@/services/analytics";
import { productoActual } from "@/services/origen";
import {
  montarEnSombra,
  saltosInternos,
} from "@/components/botpie-assistant/shadowMount";
import { arrancarLanding } from "@/components/utopie/landing/landingEngine";

// Lo que cambia respecto de la propuesta. Va después de la hoja.
const AJUSTES =
  // Con sesión, la cabecera y el "¿Qué buscas?" son los de la app.
  ".con-sesion .top{display:none}";

export default {
  name: "LandingPage",
  props: {
    bannerLoaded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { error: false };
  },
  computed: {
    token() {
      return this.$store.state.login.token;
    },
  },
  watch: {
    token() {
      this.ajustarSesion();
    },
  },
  created() {
    this.limpiezas = [];
  },
  mounted() {
    if (this.token) {
      this.$connect();
    }
    informationService
      .getLanding()
      .then((payload) => this.montar(payload))
      .catch(() => {
        this.error = true;
      });
  },
  // Default.vue la guarda con keep-alive al ir a un servicio. Mientras no está
  // en pantalla las escenas se detienen solas (sus observadores ya no la ven);
  // al volver, el motor vuelve a medir por si la ventana cambió de tamaño.
  activated() {
    if (this.cuerpo) window.dispatchEvent(new Event("resize"));
  },
  beforeDestroy() {
    this.limpiezas.forEach((limpiar) => limpiar());
    this.limpiezas = [];
  },
  methods: {
    montar(payload) {
      if (this._isDestroyed) return;
      const montado = montarEnSombra(this.$refs.host, payload, {
        ajustes: AJUSTES,
        clase: "landing",
      });
      if (!montado) {
        this.error = true;
        return;
      }
      this.raiz = montado.raiz;
      this.cuerpo = montado.cuerpo;
      this.ajustarSesion();

      const nodo = this.raiz.getElementById("landing-img");
      const IMG = nodo ? JSON.parse(nodo.textContent) : {};
      this.limpiezas.push(
        // `entrar` antes que `saltosInternos`: con sesión, un ícono que apunta
        // a `#plataforma` redirige, y no debe arrancar además el scroll.
        this.entrar(this.raiz),
        saltosInternos(this.raiz),
        arrancarLanding({
          sombra: this.raiz,
          cuerpo: this.cuerpo,
          IMG,
          // Con sesión la cabecera fija es la de la app: las franjas de
          // producto se pegan debajo de ella y no bajo la de la portada.
          altoCabecera: () => {
            const h = document.querySelector(".header-general-container");
            return h ? h.offsetHeight : 0;
          },
          // El formulario de contacto. api.js le pone el captcha.
          //
          // `datos` son los campos estructurados del formulario. Van además
          // del asunto y el cuerpo, no en su lugar: el correo sigue saliendo
          // igual, y con esto el servidor puede dejar la SolicitudCotizacion
          // y mandarle el Lead a Meta. `producto` es la clave de la página,
          // que el servidor no puede averiguar sola —la portada vive después
          // del `#`—, y acá es siempre el Sistema PIE.
          enviar: (subject, message, datos) =>
            informationService
              .sendEmail(
                Object.assign(
                  { subject, message, producto: productoActual("redpie") },
                  datos || {}
                )
              )
              .then((respuesta) => {
                // La mitad de GA4 del mismo lead. Se emite al resolver y no
                // al apretar: un envío que falló abre el `mailto:` y no es una
                // conversión nuestra.
                trackLeadDeLaPortada();
                return respuesta;
              }),
        })
      );
    },
    ajustarSesion() {
      if (this.cuerpo) this.cuerpo.classList.toggle("con-sesion", !!this.token);
    },
    // Los enlaces a la app.
    // · Con sesión, los de un servicio (`data-servicio`) van directo a él: la
    //   Plataforma PIE a /redpie y el resto a su chatbot, por el mismo
    //   `changeView` de las burbujas del layout. Sin pasar por la demo.
    // · Sin sesión, "Probar gratis" y "Conocer más" van a
    //   /utopie/#/<producto>-demo, con o sin `?login=1`: se navega con el
    //   router, sin recargar el SPA, y el modal lo abre Default.vue al ver
    //   `login` en la ruta. Los íconos del inicio bajan a la sección del
    //   producto, igual que la guía lateral (eso lo hace `saltosInternos`).
    // · "Iniciar sesión" apunta a /utopie/, que dentro de la app es recargar la
    //   portada: sin sesión abre el login.
    // · El logo apunta al inicio de la app (/utopie/#/), que es esta misma
    //   portada: sube al principio.
    // Ctrl/cmd-clic sigue abriendo una pestaña nueva.
    entrar(raiz) {
      const alClic = (e) => {
        if (e.button || e.metaKey || e.ctrlKey || e.shiftKey) return;
        const a = e.target.closest && e.target.closest("a[href]");
        if (!a) return;
        // Se mide siempre; lo que cambia con sesión es QUÉ se mide, y eso lo
        // decide `medirClicDePortada`: calla la navegación (`demo`) y deja
        // pasar las puertas de contacto (`contacto_colegio`), porque quien ya
        // tiene cuenta y escribe por WhatsApp puede ser un colegio viniendo a
        // contratar. Distinguirlo acá obligaría a reconocer WhatsApp y
        // `mailto:` en dos sitios.
        //
        // También decide QUÉ vale cada enlace mirando a dónde va, no su
        // `data-servicio`: de los 31 enlaces marcados de la portada solo 20
        // llevan a una demo, y los otros once se contaban igual.
        //
        // Sigue midiendo ANTES de decidir a dónde va: tres de las cuatro ramas
        // de abajo navegan o emiten, y la de la plataforma se lleva la página
        // entera. Medir después de esa rama sería no medir.
        //
        // (Sin nombrar acá la línea que navega: `test_el_listener_mide_antes_
        // de_decidir_a_donde_va` busca su texto para comprobar que la medición
        // va primero, y un comentario que la nombre antes lo engaña.)
        medirClicDePortada(a, !!this.token);
        const servicio = a.getAttribute("data-servicio");
        const href = a.getAttribute("href");
        if (href === "/utopie/#/") {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else if (this.token && servicio) {
          e.preventDefault();
          if (servicio === "redpie") window.location.href = "/redpie#/";
          else this.$emit("try", servicio);
        } else if (href.startsWith("/utopie/#/")) {
          e.preventDefault();
          this.$router.push(href.slice("/utopie/#".length)).catch(() => {});
        } else if (href === "/utopie/" && !this.token) {
          e.preventDefault();
          this.$emit("open-login", { register: false });
        }
      };
      raiz.addEventListener("click", alClic);
      return () => raiz.removeEventListener("click", alClic);
    },
  },
};
</script>

<style lang="scss">
.landing-wrap {
  max-width: 100%;
}

.landing-host {
  display: block;
}

.landing-error {
  margin: 0;
  padding: 3rem 1.5rem;
  text-align: center;
  color: #5c636d;
}
</style>
